import { LanguageMap, SubsidiaryMap } from '@coolblue-development/constants';
import { generateUrl } from '@coolblue-development/next-routing/server';
import { WorkArea } from '@graphQLTypes';

export const getDepartmentPageUrls = (): Record<
  'culture' | WorkArea,
  Partial<SubsidiaryMap<Partial<LanguageMap<Promise<string | undefined>>>>>
> => ({
  culture: {
    nl: {
      nl: generateUrl('careersWebsite.content.531bb4c4-d57c-4abd-acfe-fb015e2676e0'),
      en: generateUrl('careersWebsite.content.531bb4c4-d57c-4abd-acfe-fb015e2676e0')
    },
    be: {
      nl: generateUrl('careersWebsite.content.531bb4c4-d57c-4abd-acfe-fb015e2676e0'),
      fr: generateUrl('careersWebsite.content.531bb4c4-d57c-4abd-acfe-fb015e2676e0')
    },
    de: {
      de: generateUrl('careersWebsite.content.0b74b089-c8ab-4d3c-a788-b8f6e8c5cbfd')
    }
  },
  [WorkArea.CustomerService]: {
    nl: {
      nl: generateUrl('careersWebsite.category.5fc894b6-0a08-48d0-a734-a2c2604485da')
    },
    de: {
      de: generateUrl('careersWebsite.category.702cbae3-8bf5-456c-8550-3e77f1296311')
    }
  },
  [WorkArea.Delivery]: {
    nl: {
      nl: generateUrl('careersWebsite.category.14cdfd25-204b-4c18-a093-9fe686fc42d3')
    },
    be: {
      nl: generateUrl('careersWebsite.category.a413626c-6cee-4ef2-b470-0da5f416a147.nl'),
      fr: generateUrl('careersWebsite.category.a413626c-6cee-4ef2-b470-0da5f416a147.fr')
    },
    de: {
      de: generateUrl('careersWebsite.category.54ef6f1d-c33e-4a44-8868-3d7e59c1a603')
    }
  },
  [WorkArea.Office]: {
    nl: {
      nl: generateUrl('careersWebsite.category.8a8462eb-8c37-4e07-9346-6c6848b8fb18')
    },
    be: {
      nl: generateUrl('careersWebsite.category.5703153a-a084-4ebd-bbe9-d58a3cc638e6.nl'),
      fr: generateUrl('careersWebsite.category.5703153a-a084-4ebd-bbe9-d58a3cc638e6.fr')
    },
    de: {
      de: generateUrl('careersWebsite.category.989f339f-4653-410a-b3fa-d68f1f9d7336')
    }
  },
  [WorkArea.Store]: {
    nl: {
      nl: generateUrl('careersWebsite.category.5c08f405-d97c-42a5-9ed4-5b6013510364')
    },
    be: {
      nl: generateUrl('careersWebsite.category.2dd87140-5dd3-43ba-9d8d-321e9a80ed00')
    },
    de: {
      de: generateUrl('careersWebsite.category.fa6ff512-6719-45e2-98fb-80e582297b64')
    }
  },
  [WorkArea.Warehouse]: {
    nl: {
      nl: generateUrl('careersWebsite.category.617256c5-8fd8-4aa3-923f-d151db84bd87')
    },
    be: {
      fr: generateUrl('careersWebsite.category.88a7b75a-7bf5-4699-8da1-99bfbf6379cd')
    }
  }
});

export const DEPARTMENT_INFO: Record<WorkArea, { imageId: string }> = {
  [WorkArea.CustomerService]: { imageId: '948368299' },
  [WorkArea.Delivery]: { imageId: '1433864229' },
  [WorkArea.Office]: { imageId: '515076306' },
  [WorkArea.Store]: { imageId: '1878011052' },
  [WorkArea.Warehouse]: { imageId: '1312598913' }
} as const;

export const ANALYTICS_DEPARTMENT_SUBFEATURES: Record<WorkArea | 'culture', string> = {
  [WorkArea.CustomerService]: 'customer_service',
  [WorkArea.Delivery]: 'delivery',
  [WorkArea.Office]: 'office',
  [WorkArea.Store]: 'stores',
  [WorkArea.Warehouse]: 'warehouse',
  culture: 'culture'
};

export const FULL_GRID_HEIGHT_OPTIONS = {
  1440: '480px',
  1280: '464px',
  1024: 'auto'
} as const;

export const GRID_TEMPLATE_COLUMN_OPTIONS = {
  1440: `repeat(2, minmax(100px, 1fr)) 310px 254px`,
  1280: `repeat(2, minmax(100px, 1fr)) 298px 244px`,
  1024: '1fr'
} as const;

export const GRID_TEMPLATE_ROW_OPTIONS = {
  1440: `repeat(10, 33.6px)`,
  1280: `repeat(10, 32px)`,
  1024: '104px',
  '1024_culture': '312px'
} as const;

export const GRID_CONFIGURATION: Record<number, { gridColumn: string; gridRow: string }[]> = {
  6: [
    {
      gridColumn: '3',
      gridRow: '1 / span 10'
    },
    {
      gridColumn: '1',
      gridRow: '1 / span 6'
    },
    {
      gridColumn: '2',
      gridRow: '1 / span 6'
    },
    {
      gridColumn: '4',
      gridRow: '1 / span 5'
    },
    {
      gridColumn: '4',
      gridRow: '6 / span 5'
    },
    {
      gridColumn: '1 / span 2',
      gridRow: '7 / span 4'
    }
  ],
  5: [
    {
      gridColumn: '3',
      gridRow: '1 / span 10'
    },
    {
      gridColumn: '1 / span 2',
      gridRow: '1 / span 6'
    },
    {
      gridColumn: '4',
      gridRow: '1 / span 5'
    },
    {
      gridColumn: '4',
      gridRow: '6 / span 5'
    },
    {
      gridColumn: '1 / span 2',
      gridRow: '7 / span 4'
    }
  ],
  4: [
    {
      gridColumn: '3',
      gridRow: '1 / span 10'
    },
    {
      gridColumn: '1 / span 2',
      gridRow: '1 / span 6'
    },
    {
      gridColumn: '4',
      gridRow: '1 / span 10'
    },
    {
      gridColumn: '1 / span 2',
      gridRow: '7 / span 4'
    }
  ],
  3: [
    {
      gridColumn: '3 / span 2',
      gridRow: '1 / span 10'
    },
    {
      gridColumn: '1 / span 2',
      gridRow: '1 / span 6'
    },
    {
      gridColumn: '1 / span 2',
      gridRow: '7 / span 4'
    }
  ],
  2: [
    {
      gridColumn: '1 / span 2',
      gridRow: '1 / span 10'
    },
    {
      gridColumn: '3 / span 2',
      gridRow: '1 / span 10'
    }
  ],
  1: [
    {
      gridColumn: '1 / span 4',
      gridRow: '1 / span 10'
    }
  ]
} as const;
